import auth, { getCredentials } from './cognito.js';

class DatabaseService {
  constructor(baseUrl = '/api') {
    this.baseUrl = baseUrl;
  }

  async request(endpoint, body = {}, timeoutMs = 30000) {
    try {
      // Ensure we have valid credentials before making request
      let credentials = AWS.config.credentials;
      
      if (!credentials || (credentials.needsRefresh && credentials.needsRefresh())) {
        credentials = await getCredentials();
        AWS.config.credentials = credentials;
      }
      
      // Validate credentials exist and have required properties
      if (!credentials || !credentials.accessKeyId || !credentials.secretAccessKey) {
        throw new Error('Invalid AWS credentials - missing required properties');
      }
      
      const request = new AWS.HttpRequest(`${this.baseUrl}${endpoint}`, 'ap-southeast-2');
      request.method = 'POST';
      request.body = JSON.stringify(body);
      request.headers['Content-Type'] = 'application/json';
      request.headers['Host'] = new URL(`${this.baseUrl}${endpoint}`).host;
      
      const signer = new AWS.Signers.V4(request, 'execute-api');
      signer.addAuthorization(credentials, new Date());

      // ST4S (design §4.3): attach the Cognito ID token so the Lambda can
      // verify the caller's identity server-side. It MUST go in a dedicated
      // header, NOT Authorization: SigV4's addAuthorization() writes the
      // request signature INTO the Authorization header, so reusing it for the
      // ID token overwrites the signature and API Gateway rejects the request
      // with 403 "Invalid key=value pair ... in Authorization header". We use
      // the custom X-Id-Token header, which is not part of the SigV4 signed set
      // and does not collide with the signature. Added after signing; only when
      // a session ID token exists (logged-in reporting users) — guest/unauth
      // and other portals send no token. The integration template forwards
      // X-Id-Token into event.context['auth-token'].
      const idToken = auth?.session?.getIdToken?.().getJwtToken?.();
      if (idToken) {
        request.headers['X-Id-Token'] = idToken;
      }

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: request.method,
        headers: request.headers,
        body: request.body,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      
      const data = await response.json();

      // The Lambda status gate (and other server-side failures) can return
      // HTTP 200 with an ERROR body instead of data, e.g.
      //   { "errorMessage": "invalid token", "errorType": "ValueError", ... }
      // Because the HTTP status is 200 the !response.ok check above does not
      // catch it, so without this guard the error object is handed back to
      // callers as if it were a result. Downstream code then runs array/object
      // operations on it (e.g. data.reduce over Items) and throws deep inside a
      // render, blanking the whole app. Detect the Lambda error shape — a
      // non-null, non-array object carrying an errorMessage — and throw so the
      // caller's try/catch handles it gracefully. Legitimate responses never
      // match: reads return arrays, count returns a number, aggregate returns
      // an array — none have an errorMessage property.
      if (data && typeof data === 'object' && !Array.isArray(data) && 'errorMessage' in data) {
        const err = new Error(data.errorMessage || 'Database request failed');
        if (data.errorType) err.errorType = data.errorType;
        throw err;
      }

      return data;
      
    } catch (err) {
      if (err.name === 'AbortError') {
        throw new Error(`Request timeout after ${timeoutMs}ms`);
      }
      console.error('Database request failed:', err);
      throw err;
    }
  }

  collection(name) {
    return new Collection(name, this);
  }
}

// Rate+concurrency limiter to prevent overwhelming the API
class ConcurrencyLimiter {
  constructor(maxConcurrent = 3, minDelayMs = 200) {
    this.max = maxConcurrent;
    this.minDelay = minDelayMs;
    this.running = 0;
    this.queue = [];
    this.lastDispatch = 0;
  }

  async run(fn) {
    while (this.running >= this.max) {
      await new Promise(r => this.queue.push(r));
    }
    // Enforce minimum gap between dispatches
    const now = Date.now();
    const wait = this.minDelay - (now - this.lastDispatch);
    if (wait > 0) await new Promise(r => setTimeout(r, wait));
    this.lastDispatch = Date.now();
    this.running++;
    try {
      return await fn();
    } finally {
      this.running--;
      if (this.queue.length > 0) this.queue.shift()();
    }
  }
}

export const apiLimiter = new ConcurrencyLimiter(20, 50);

class Collection {
  constructor(name, db) {
    this.name = name;
    this.db = db;
  }

  async find(query = {}, options = {}) {
    const { sort, limit, skip, ...projection } = options;
    return await this._chunkedFind(query, options);
  }

  async _chunkedFind(query = {}, options = {}) {
    const { sort, limit: requestedLimit, skip: requestedSkip = 0, _chunkSize, ...projection } = options;
    
    // Adaptive chunk sizing configuration
    let chunkSize = _chunkSize || 200;
    const maxChunkSize = 500;
    const minChunkSize = 10;
    const growthFactor = 1.5;
    
    let allData = [];
    let skip = requestedSkip;
    const totalLimit = requestedLimit || Infinity;
    const maxRetries = 3;
    let consecutiveFailures = 0;
    
    while (allData.length < totalLimit) {
      const currentLimit = Math.min(chunkSize, totalLimit - allData.length);
      let success = false;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const chunk = await apiLimiter.run(() => this.db.request(`${this.name}/read`, {
            operation_type: 'bulk',
            filter: query,
            ...(Object.keys(projection).length && { projection }),
            ...(sort && { sort: Object.entries(sort) }),
            limit: currentLimit,
            skip
          }, 30000 + (attempt * 15000)));
          
          if (chunk.length === 0) return allData;
          allData = allData.concat(chunk);
          skip += currentLimit;
          success = true;
          consecutiveFailures = 0;
          
          // Grow chunk size on success, capped at maximum
          chunkSize = Math.min(maxChunkSize, Math.floor(chunkSize * growthFactor));
          break;
        } catch (chunkErr) {
          console.warn(`Chunk fetch failed at skip ${skip} (attempt ${attempt + 1}/${maxRetries}):`, chunkErr.message);
          
          const isTimeout = chunkErr.message?.includes('timeout');
          const isPayloadSize = chunkErr.message?.includes('payload size');
          
          if (isTimeout || isPayloadSize) {
            consecutiveFailures++;
            chunkSize = Math.floor(chunkSize / 2);
            
            // If chunk size falls below minimum and request still fails, abort with error
            if (chunkSize < minChunkSize) {
              const errorMsg = `Chunk fetch aborted: chunk size reduced below minimum threshold (${minChunkSize}) at skip ${skip} after ${consecutiveFailures} consecutive failures`;
              console.error(errorMsg);
              throw new Error(errorMsg);
            }
            
            // Retry the same offset with smaller chunk (break out of retry loop to re-enter while loop)
            break;
          }
          
          // For other errors, use exponential backoff and retry
          if (attempt < maxRetries - 1) {
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          } else {
            consecutiveFailures++;
            console.error(`Chunk fetch permanently failed at skip ${skip} after ${maxRetries} attempts`);
          }
        }
      }
      
      if (!success) break;
    }
    
    return allData;
  }

  async count(query = {}) {
    return apiLimiter.run(() => this.db.request(`${this.name}/count`, { 
      filter: query 
    }));
  }

  async countMany(surveyIds, filter = {}) {
    return apiLimiter.run(() => this.db.request(`${this.name}/count-many`, {
      surveyIds,
      filter
    }));
  }

  async aggregate(pipeline, timeoutMs = 90000) {
    return this.db.request(`${this.name}/aggregate`, {
      pipeline: pipeline
    }, timeoutMs);
  }

  async reportData(surveyId, excludeItemIds = [], timeoutMs = 90000) {
    return this.db.request(`${this.name}/report-data`, {
      surveyId,
      excludeItemIds
    }, timeoutMs);
  }

  async insertOne(doc) {
    return this.db.request(`${this.name}/create`, {
        operation_type: 'atomic',
        document: doc
    });
  }

  async updateOne(filter, update) {
    return this.db.request(`${this.name}/update`, {
        operation_type: 'atomic',
        filter,
        update
    });
  }

  async deleteOne(filter) {
    return this.db.request(`${this.name}/delete`, {
        operation_type: 'atomic',
        filter
    });
  }

  async findWithProgress(query = {}, options = {}, progressCallback = null) {
    const { sort, limit: requestedLimit, skip: requestedSkip = 0, ...projection } = options;
    let chunkSize = 100;
    let allData = [];
    let skip = requestedSkip;
    const totalLimit = requestedLimit || Infinity;
    const maxRetries = 3;
    
    const totalCount = progressCallback ? await this.count(query) : 0;
    
    while (allData.length < totalLimit) {
      const currentLimit = Math.min(chunkSize, totalLimit - allData.length);
      let success = false;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const chunk = await apiLimiter.run(() => this.db.request(`${this.name}/read`, {
            operation_type: 'bulk',
            filter: query,
            ...(Object.keys(projection).length && { projection }),
            ...(sort && { sort: Object.entries(sort) }),
            limit: currentLimit,
            skip
          }, 30000 + (attempt * 15000)));
          
          if (chunk.length === 0) return allData;
          allData = allData.concat(chunk);
          skip += currentLimit;
          success = true;
          
          if (progressCallback && totalCount > 0) {
            const progress = Math.min(100, (allData.length / Math.min(totalCount, totalLimit)) * 100);
            progressCallback(progress, allData.length, totalCount);
          }
          break;
        } catch (chunkErr) {
          console.warn(`Chunk fetch failed at skip ${skip} (attempt ${attempt + 1}/${maxRetries}):`, chunkErr.message);
          if (attempt < maxRetries - 1) {
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
            if (chunkErr.message?.includes('timeout') && chunkSize > 10) {
              chunkSize = Math.max(10, Math.floor(chunkSize * 0.6));
            }
          }
        }
      }
      
      if (!success) break;
    }
    
    return allData;
  }
}

const getApiStage = () => {
  const hostname = window.location.hostname;
  return (hostname === 'localhost' || hostname.includes('dev.')) ? 'test' : 'prod';
};

export default new DatabaseService(`https://rkn22akwp8.execute-api.ap-southeast-2.amazonaws.com/${getApiStage()}/`);
